import React, { useState, useEffect } from 'react';
import './Header.css';
import logoImg from '../purayida-krishi-logo-file.png';

const Header = ({ language, toggleLanguage, showCartIcon = false, cartCount = 0, onCartClick = () => {} }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('hero');

  const content = {
    en: {
      home: 'Home',
      blog: 'Blog',
      mystory: 'My Story',
      Services: 'Services',
      bookstore: 'Book Store',
      gallery: 'Gallery',
      faq: 'FAQ',
      contact: 'Contact'
    },
    ml: {
      home: 'ഹോം',
      blog: 'ബ്ലോഗ്',
      mystory: 'എന്റെ കഥ',
      Services: 'സേവനം',
      bookstore: 'പുസ്തക സ്റ്റോർ',
      gallery: 'ഗാലറി',
      faq: 'ചോദ്യോത്തരങ്ങൾ',
      contact: 'ബന്ധപ്പെടുക'
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    
    const scrollToSection = () => {
      const el = document.getElementById(sectionId);
      if (el) {
        // Calculate offset: 10vh (top ad) + header height (~80px) + small padding
        const topAdHeight = window.innerHeight * 0.1; // 10vh
        const headerHeight = 80; // approximate header height
        const offset = topAdHeight + headerHeight + 20; // +20px padding
        
        const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };
    
    // First navigate to home if on a different route
    if (window.location.hash && window.location.hash !== '#' && !window.location.hash.startsWith('#' + sectionId)) {
      window.location.hash = '#';
      // Wait for navigation then scroll
      setTimeout(scrollToSection, 100);
    } else {
      // Already on home page, just scroll
      scrollToSection();
    }
    setIsMenuOpen(false);
  };

  // Track current section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('home');
      const agroecologySection = document.getElementById('agroecology');
      
      if (heroSection && agroecologySection) {
        const heroRect = heroSection.getBoundingClientRect();
        const agroRect = agroecologySection.getBoundingClientRect();
        
        // If hero section is visible (top is above middle of screen)
        if (heroRect.top <= window.innerHeight / 2 && heroRect.bottom >= window.innerHeight / 2) {
          setCurrentSection('hero');
        }
        // If agroecology section is visible (top is above middle of screen)
        else if (agroRect.top <= window.innerHeight / 2 && agroRect.bottom >= window.innerHeight / 2) {
          setCurrentSection('agroecology');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = () => {
    const scrollToSectionWithOffset = (sectionId) => {
      const el = document.getElementById(sectionId);
      if (el) {
        // Calculate offset: 10vh (top ad) + header height (~80px) + small padding
        const topAdHeight = window.innerHeight * 0.1; // 10vh
        const headerHeight = 80; // approximate header height
        const offset = topAdHeight + headerHeight + 20; // +20px padding
        
        const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };

    if (currentSection === 'hero') {
      // Navigate to agroecology section
      scrollToSectionWithOffset('agroecology');
    } else {
      // Navigate back to hero section
      scrollToSectionWithOffset('home');
    }
  };


  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <div className="logo">
            <img 
              src={logoImg} 
              alt="Purayidakrishi Logo" 
              className="logo-img"
            />
            <div className="logo-text" style={{display: 'none'}}>
              <h2>Purayidakrishi</h2>
              <span>Homestead Agroecology</span>
            </div>
          </div>
          
          <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
            <ul className="nav-list">
              <li><a href="#home" className="nav-link" onClick={(e) => handleNavClick(e, 'home')}>{content[language].home}</a></li>
              <li><a href="#blog" className="nav-link" onClick={(e) => handleNavClick(e, 'blog')}>{content[language].blog}</a></li>
              <li><a href="#mystory" className="nav-link" onClick={(e) => handleNavClick(e, 'mystory')}>{content[language].mystory}</a></li>
              <li><a href="#services" className="nav-link" onClick={(e) => handleNavClick(e, 'services')}>{content[language].Services}</a></li>
              <li><a href="#book-store" className="nav-link" onClick={(e) => handleNavClick(e, 'book-store')}>{content[language].bookstore}</a></li>
              <li><a href="#gallery" className="nav-link" onClick={(e) => handleNavClick(e, 'gallery')}>{content[language].gallery}</a></li>
              <li><a href="#faq" className="nav-link" onClick={(e) => handleNavClick(e, 'faq')}>{content[language].faq}</a></li>
              <li><a href="#contact" className="nav-link" onClick={(e) => handleNavClick(e, 'contact')}>{content[language].contact}</a></li>
            </ul>
          </nav>
          
          <div className="header-actions">
            {showCartIcon && (
              <button
                aria-label="Cart"
                onClick={onCartClick}
                style={{ position: 'relative', marginRight: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 4h-2l-1 2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h9v-2h-9l1.1-2h6.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1h-14zm-2 16c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2zm10 0c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2z" />
                </svg>
                {cartCount > 0 && (
                  <span style={{ position: 'absolute', top: -6, right: -6, background: '#2d5016', color: '#fff', borderRadius: 999, padding: '2px 6px', fontSize: 11, fontWeight: 600 }}>
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            <div className="social-media">
              <a href="https://www.instagram.com/purayidakrishi25?igsh=cnhuOWI1dDhsczB2" target="_blank" rel="noopener noreferrer" className="social-link">
                <svg viewBox="0 0 24 24" fill="currentColor" className="social-icon">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://www.facebook.com/share/19g2Awgs3p/" target="_blank" rel="noopener noreferrer" className="social-link">
                <svg viewBox="0 0 24 24" fill="currentColor" className="social-icon">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.youtube.com/@purayidakrishi" target="_blank" rel="noopener noreferrer" className="social-link">
                <svg viewBox="0 0 24 24" fill="currentColor" className="social-icon">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/in/illiyas-kp-759350333?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app" target="_blank" rel="noopener noreferrer" className="social-link">
                <svg viewBox="0 0 24 24" fill="currentColor" className="social-icon">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
            
            <button 
              className="section-toggle"
              onClick={toggleSection}
            >
              {currentSection === 'hero' ? 'English' : 'മലയാളം'}
            </button>
            
            <button 
              className="menu-toggle"
              onClick={toggleMenu}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
